<template>
  <div class="dashboard" v-if="user">
    <b-row>
      <b-col cols="12">
        <filters
            :disabled="loading"
            :downloading="downloading"
            :show-license-type-filter="true"
            @input="selectFilter"
            @select:filter="selectFilter"
            @apply="apply"
            @download="downloadCsv"
        />
      </b-col>
    </b-row>

    <!--   Course type -->
    <b-row>
      <b-col>
        <main-table
            :ref="'by_course_type'"
            :list="appData.by_course_type"
            :fields="courseTypeFields"
            :loading="loading"
            nested-rows-key="children"
            title="By Course Type"
            @click:update="updateTable('by_course_type')"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <main-table
            :ref="'by_course_type_total'"
            :list="appData.by_course_type_total"
            :fields="courseTypeFields"
            :loading="loading"
            nested-rows-key="children"
            title="By Course Type (total)"
            @click:update="updateTable('by_course_type_total')"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <main-table
            :ref="'by_promo_campaign'"
            :list="appData.by_promo_campaign"
            :fields="coursePromoCampaignFields"
            :loading="loading"
            title="By Promo Campaign"
            @click:update="updateTable('by_promo_campaign')"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <main-table
            :ref="'by_driving_school'"
            :list="appData.by_driving_school"
            :fields="drivingSchoolFields"
            :loading="loading"
            sort-value="total_all_in"
            title="By Driving School"
            @click:update="updateTable('by_driving_school')"
        />
      </b-col>
    </b-row>

    <!--  Exam type  -->
    <b-row>
      <b-col>
        <main-table
            :ref="'by_exam_type'"
            :list="appData.by_exam_type"
            :fields="examTypeFields"
            :loading="loading"
            title="By Exam Type"
            @click:update="updateTable('by_exam_type')"
        />
      </b-col>
    </b-row>

    <!--  Exam Results  -->
    <b-row>
      <b-col>
        <main-table
            :ref="'by_exam_results'"
            :list="appData.by_exam_results"
            :fields="examResultsFields"
            :loading="loading"
            sort-value="status"
            title="By Exam Results"
            @click:update="updateTable('by_exam_results')"
        />
      </b-col>
    </b-row>

    <!--   Extra exams les | Extra exams online -->
    <b-row>
      <b-col>
        <main-table
            :ref="'by_extra_exams_les'"
            :list="appData.by_extra_exams_les"
            :fields="extraExamFields"
            :loading="loading"
            title="Extra exams (LES)"
            @click:update="updateTable('by_extra_exams_les')"
        />
      </b-col>
      <b-col>
        <main-table
            :ref="'by_extra_exams_online'"
            :list="appData.by_extra_exams_online"
            :fields="extraExamFields"
            :loading="loading"
            title="Extra exams (ONLINE)"
            @click:update="updateTable('by_extra_exams_online')"
        />
      </b-col>
    </b-row>

    <!--   Online Packages | Categories -->
    <b-row>
      <b-col>
        <main-table
            :ref="'by_package'"
            :list="appData.by_package"
            :fields="packageFields"
            :loading="loading"
            title="Online packages"
            @click:update="updateTable('by_package')"
        />
      </b-col>
      <b-col>
        <main-table
            :ref="'by_course_option'"
            :list="appData.by_course_option"
            :fields="categoryFields"
            :loading="loading"
            title="Category (paid)"
            @click:update="updateTable('by_course_option')"
        />
      </b-col>
    </b-row>

    <!--   Offline packages -->
    <b-row>
      <b-col>
        <main-table
            :ref="'by_offline_day_packages'"
            :list="appData.by_offline_day_packages"
            :fields="offlineDayPackageFields"
            :loading="loading"
            title="Offline packages"
            @click:update="updateTable('by_offline_day_packages')"
        />
      </b-col>
    </b-row>

    <!--   Cities | Locations -->
    <b-row>
      <b-col>
        <main-table
            :ref="'by_city'"
            :list="appData.by_city"
            :fields="cityFields"
            :loading="loading"
            sort-value="status"
            title="By cities (paid)"
            @click:update="updateTable('by_city')"
        />
      </b-col>
      <b-col>
        <main-table
            :ref="'by_location'"
            :list="appData.by_location"
            :fields="locationFields"
            :loading="loading"
            sort-value="status"
            title="By Location (paid)"
            @click:update="updateTable('by_location')"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { BRow, BCol } from 'bootstrap-vue'
import DashboardModel from '@/models/dashboard-model'
import { UserService } from '@/services/user.service'
import { UsersService } from '@/services/users.service'
import Filters from '@/components/filters/Filters.vue'
import mainTable from '@/components/tables/MainTable.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'Dashboard',

  components: {
    BRow,
    BCol,
    Filters,
    mainTable
  },

  setup(props, ctx) {
    const userService = new UserService(ctx.root.$http);
    const usersService = new UsersService(ctx.root.$http);

    return {
      userService,
      usersService,
      courseTypeFields: [
        { key: 'name', label: 'Course Type' },
        { key: 'total' },
        { key: 'unique' },
        { key: 'paid' },
        { key: 'ratio', label: 'Paid/Unique' },
        { key: 'paid_wn_15m', label: 'Paid w/n 15m' },
        { key: 'paid_wn_24h', label: 'Paid w/n 24h' }
      ],
      coursePromoCampaignFields: [
        { key: 'campaign_type', label: 'Campaign Type', sortable: true },
        { key: 'source', label: 'Source', sortable: true },
        { key: 'promo_code', label: 'Promo Code', sortable: true },
        { key: 'total_offline', label: 'Total Offline', sortable: true },
        { key: 'unique_offline', label: 'Unique Offline', sortable: true },
        { key: 'paid_offline', label: 'Paid Offline', sortable: true },
        { key: 'total_online', label: 'Total Online', sortable: true },
        { key: 'unique_online', label: 'Unique Online', sortable: true },
        { key: 'paid_online', label: 'Paid Online', sortable: true },
      ],
      drivingSchoolFields: [
        { key: 'campaign_type', label: 'Driving schools name', sortable: true },
        { key: 'source', label: 'Source', sortable: true },
        { key: 'promo_code', label: 'Promo Code', sortable: true },
        { key: 'total_offline', label: 'Total Offline', sortable: true },
        { key: 'unique_offline', label: 'Unique Offline', sortable: true },
        { key: 'paid_offline', label: 'Paid Offline', sortable: true },
        { key: 'total_online', label: 'Total Online', sortable: true },
        { key: 'unique_online', label: 'Unique Online', sortable: true },
        { key: 'paid_online', label: 'Paid Online', sortable: true },
        { key: 'total_all_in', label: 'Total All-In', sortable: true },
        { key: 'unique_all_in', label: 'Unique All-In', sortable: true },
        { key: 'paid_all_in', label: 'Paid All-In', sortable: true },
      ],
      examTypeFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'new_offline', label: 'New unique offline', sortable: true },
        { key: 'reapply_offline', label: 'Reapply unique offline', sortable: true },
        { key: 'new_paid_offline', label: 'New paid offline', sortable: true },
        { key: 'reapply_paid_offline', label: 'Reapply paid offline', sortable: true },
        { key: 'new_paid_unique_offline', label: 'New paid/unique offline', sortable: true },
        { key: 'reapply_paid_unique_offline', label: 'Reapply paid/unique offline', sortable: true },
        { key: 'new_online', label: 'New unique online', sortable: true },
        { key: 'reapply_online', label: 'Reapply unique online', sortable: true },
        { key: 'new_paid_online', label: 'New paid online', sortable: true },
        { key: 'reapply_paid_online', label: 'Reapply paid online', sortable: true },
        { key: 'new_paid_unique_online', label: 'New paid/unique online', sortable: true },
        { key: 'reapply_paid_unique_online', label: 'Reapply paid/unique online', sortable: true },
        { key: 'total', sortable: true }
      ],
      examResultsFields: [
        { key: 'status', sortable: true },
        { key: 'count', sortable: true },
        { key: 'ratio', sortable: true }
      ],
      categoryFields: [
        { key: 'name', sortable: true },
        { key: 'count', sortable: true }
      ],
      cityFields: [
        { key: 'name', sortable: true },
        { key: 'count', sortable: true }
      ],
      locationFields: [
        { key: 'name', sortable: true },
        { key: 'count', sortable: true }
      ],
      packageFields: [
        { key: 'name', sortable: true },
        { key: 'payments_count', label: 'Amount', sortable: true },
        { key: 'money', sortable: true }
      ],
      offlineDayPackageFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'amount', label: 'Amount', sortable: true },
        { key: 'money', label: 'Money', sortable: true }
      ],
      extraExamFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'money', label: 'Money', sortable: true },
        { key: 'payments_count', label: 'Payments count', sortable: true }
      ],
    }
  },

  data: () => ({
    loading: false,
    downloading: false,
    appData: null,
    initialAppData: null,
    filters: {
      date_after: '',
      date_before: ''
    }
  }),

  computed: {
    ...mapGetters({
      user: 'user/user'
    }),

    hasDateAfter() {
      let _getCookies = this.$cookies.get('filters')
      return !!(_getCookies && _getCookies.date_after);
    },

    hasDateBefore() {
      let _getCookies = this.$cookies.get('filters')
      return !!(_getCookies && _getCookies.date_before);
    }
  },

  created() {
    this.appData = new DashboardModel()
  },

  mounted() {
    // this.preSelectDateFilters()
  },

  methods: {
    async preSelectDateFilters() {
      let _today = this.$moment().format('YYYY-MM-DD')
      let _getCookies = this.$cookies.get('filters')

      if (!this.hasDateAfter || !this.hasDateBefore) {
        await this.selectFilter({
          date_after: _today,
          date_before: _today
        })
      } else {
        await this.selectFilter({ date_after: _getCookies.date_after, date_before: _getCookies.date_before })
      }
      await this.apply();
    },

    async updateTable(key) {
      try {
        this.loading = true
        let res = await this.usersService.getStatistics({ ...this.$route.query, blocks: key })
        this.appData = new DashboardModel({ ...this.initialAppData, ...res })
      } catch (error) {
        console.error(error)
        this.appData = new DashboardModel({ ...this.initialAppData, [key]: undefined })
      } finally {
        this.loading = false
      }
    },

    async fetchData(query) {
      try {
        this.loading = true
        let res = await this.usersService.getStatistics({ ...this.$route.query, ...query })
        this.appData = new DashboardModel({ ...this.appData, ...res })
        this.initialAppData = {...res}
      } catch (error) {
        console.error(error)
        this.appData = new DashboardModel()
        this.initialAppData = null
      } finally {
        this.loading = false
      }
    },

    async selectFilter(payload) {
      if (this.loading) return
      this.filters = { ...this.filters, ...payload }
      this.$cookies.set('filters', this.filters);
      await this.setQuery(this.filters)
    },

    async apply() {
      await this.fetchData()
    },

    async downloadCsv() {
      const { date_after, date_before } = this.$route.query
      if (!date_after || !date_before) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please select both start and end dates',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      try {
        this.downloading = true
        const query = { ...this.$route.query }
        const response = await this.usersService.downloadApplicationsCsv(query)
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `applications_${date_after}_${date_before}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to download report',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.downloading = false
      }
    },

    async setQuery(data) {
      const query = { ...this.$route.query, ...data };
      Object.keys(query).forEach(key => (query[key] === undefined || query[key] === null) && delete query[key]);
      await this.$router.push({ query }).catch(() => {});
    },
  }
}
</script>

<style lang="scss">
.dashboard {
  flex-direction: column;
}
</style>
